import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  FormField,
  Header,
  Input,
  SpaceBetween,
  Textarea,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationUpdateRequest,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useUpdateInvestigation,
} from "../../hooks/useInvestigations";
import { isTerminal, submitOnModEnter } from "./investigationUtils";
import { InvestigationLinkList } from "./InvestigationLinkList";
import { useAttachmentUploads } from "../../hooks/useAttachmentUploads";
import {
  AttachmentDropzone,
  attachmentPasteHandler,
} from "./attachments/AttachmentDropzone";
import { AttachmentList } from "./attachments/AttachmentList";
import type { ProposedConclusion } from "./InvestigationTrackStarsTab";
import { saveErrorText } from "./versionConflict";

/** Muted empty-state line for a section with no entries yet. */
const EmptyLine: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box color="text-status-inactive" fontSize="body-s">
    {children}
  </Box>
);

/**
 * Cancel / Save pair used by every inline form. Save is the primary action so the eye
 * lands on it; Cancel collapses the form without saving.
 */
const InlineFormActions: React.FC<{
  saveLabel: string;
  disabled: boolean;
  loading: boolean;
  onCancel: () => void;
  onSave: () => void;
}> = ({ saveLabel, disabled, loading, onCancel, onSave }) => (
  <Box float="right">
    <SpaceBetween direction="horizontal" size="xs">
      <Button variant="link" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        variant="primary"
        disabled={disabled}
        loading={loading}
        onClick={onSave}
      >
        {saveLabel}
      </Button>
    </SpaceBetween>
  </Box>
);

/**
 * The Conclusion tab: the one thing the next person hitting this population reads — a short
 * summary plus the CRs, tickets and queries that back it. Everyone may contribute; resolving
 * (on the Status card) requires both a summary and at least one link, or an override.
 *
 * Layout principle: read first, write on demand. The card shows its content and a single
 * "Add …" / "Edit …" action in the header; the form only appears once that action is clicked.
 */
interface InvestigationConclusionProps {
  stage: Stage;
  investigation: Investigation;
  currentUser: string | null;
  onError: (message: string) => void;
  /**
   * A conclusion proposed elsewhere (the TrackStars tab): pre-fills and opens the form. A new
   * object each time so the same proposal can be applied twice. Nothing is saved until Save.
   */
  proposal?: ProposedConclusion | null;
}

type ConclusionForm = "conclusion" | "link" | null;

/**
 * The one thing the next person hitting this population reads: a short summary plus the
 * CRs, tickets and queries that back it. Resolve asks for both, but an override is allowed.
 */
export const InvestigationConclusion: React.FC<
  InvestigationConclusionProps
> = ({ stage, investigation, currentUser, onError, proposal }) => {
  const update = useUpdateInvestigation(stage);
  const loading = update.isLoading;
  // The backend only lets the owner set the conclusion, and freezes it on closed cases (it can
  // only change in the request that reopens them). Everyone else sees it read-only.
  const canEditConclusion =
    !!currentUser &&
    investigation.owner === currentUser &&
    !isTerminal(investigation.status);
  const run = (request: InvestigationUpdateRequest, after?: () => void) =>
    update
      .mutateAsync({
        investigationId: investigation.id,
        request: { ...request, expectedVersion: investigation.version },
      })
      .then(() => after?.())
      .catch((err: InvestigationApiError) => onError(saveErrorText(err)));
  const saved = investigation.conclusion;
  const links = investigation.links || [];
  const [openForm, setOpenForm] = useState<ConclusionForm>(null);
  const [summary, setSummary] = useState(saved?.summary || "");
  const [rootCause, setRootCause] = useState(saved?.rootCause || "");
  const [fix, setFix] = useState(saved?.fix || "");
  const [linkLabel, setLinkLabel] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  // Screenshots/files backing the conclusion; the form starts from the saved ones.
  const uploads = useAttachmentUploads(stage, investigation.id);
  const { reset: resetUploads } = uploads;

  const startEditing = () => {
    setSummary(saved?.summary || "");
    setRootCause(saved?.rootCause || "");
    setFix(saved?.fix || "");
    resetUploads(saved?.attachments || []);
    setOpenForm("conclusion");
  };
  useEffect(() => {
    if (!proposal || !canEditConclusion) return;
    setSummary(proposal.summary);
    setRootCause(proposal.rootCause || "");
    setFix(proposal.fix || "");
    resetUploads(investigation.conclusion?.attachments || []);
    setOpenForm("conclusion");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a new proposal re-opens the form
  }, [proposal, canEditConclusion, resetUploads]);
  const closeForm = () => {
    setLinkLabel("");
    setLinkUrl("");
    setOpenForm(null);
  };

  const saveConclusion = () => {
    run(
      {
        conclusion: {
          summary: summary.trim(),
          rootCause: rootCause.trim() || undefined,
          fix: fix.trim() || undefined,
          // The full list: files removed here are dropped from the conclusion (kept in S3).
          attachments: uploads.refs,
        },
      } as InvestigationUpdateRequest,
      closeForm
    ).catch(console.error);
  };
  const saveLink = () => {
    run(
      {
        addLink: {
          // No label typed: the backend labels CR / ticket links with their id (CR-123…).
          label: linkLabel.trim() || undefined,
          url: linkUrl.trim(),
        },
      } as InvestigationUpdateRequest,
      closeForm
    ).catch(console.error);
  };
  const conclusionDisabled = !summary.trim() || loading || uploads.uploading;

  return (
    <Container
      header={
        <Header
          variant="h3"
          description="What was wrong, what fixed it, and the links that prove it."
          actions={
            openForm === null && (
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="add-plus" onClick={() => setOpenForm("link")}>
                  Add evidence link
                </Button>
                {canEditConclusion && (
                  <Button
                    iconName={saved?.summary ? "edit" : "add-plus"}
                    onClick={startEditing}
                  >
                    {saved?.summary ? "Edit conclusion" : "Write conclusion"}
                  </Button>
                )}
              </SpaceBetween>
            )
          }
        >
          Conclusion
        </Header>
      }
    >
      <SpaceBetween size="m">
        {openForm === "conclusion" && canEditConclusion ? (
          <div onPaste={attachmentPasteHandler(uploads, loading)}>
            <SpaceBetween size="s">
              <FormField
                label="Summary"
                description="One or two sentences anyone can act on."
                stretch
              >
                <Textarea
                  value={summary}
                  onChange={({ detail }) => setSummary(detail.value)}
                  onKeyDown={submitOnModEnter(
                    saveConclusion,
                    conclusionDisabled
                  )}
                  rows={2}
                  autoFocus
                />
              </FormField>
              <FormField label="Root cause" description="Optional." stretch>
                <Input
                  value={rootCause}
                  onChange={({ detail }) => setRootCause(detail.value)}
                  onKeyDown={submitOnModEnter(
                    saveConclusion,
                    conclusionDisabled
                  )}
                  placeholder="Why the messages ended up in the DLQ"
                />
              </FormField>
              <FormField label="Fix" description="Optional." stretch>
                <Input
                  value={fix}
                  onChange={({ detail }) => setFix(detail.value)}
                  onKeyDown={submitOnModEnter(
                    saveConclusion,
                    conclusionDisabled
                  )}
                  placeholder="What was changed (CR, config, data)"
                />
              </FormField>
              <FormField
                label="Attachments"
                description="Optional. Screenshots or files that back the conclusion."
                stretch
              >
                <AttachmentDropzone uploads={uploads} disabled={loading}>
                  {null}
                </AttachmentDropzone>
              </FormField>
              <InlineFormActions
                saveLabel="Save conclusion"
                disabled={!summary.trim() || uploads.uploading}
                loading={loading}
                onCancel={closeForm}
                onSave={saveConclusion}
              />
            </SpaceBetween>
          </div>
        ) : saved?.summary ? (
          <Box data-testid="saved-conclusion">
            <SpaceBetween size="s">
              <Box>
                <Box variant="awsui-key-label">Summary</Box>
                <Box>{saved.summary}</Box>
              </Box>
              {saved.rootCause && (
                <Box>
                  <Box variant="awsui-key-label">Root cause</Box>
                  <Box>{saved.rootCause}</Box>
                </Box>
              )}
              {saved.fix && (
                <Box>
                  <Box variant="awsui-key-label">Fix</Box>
                  <Box>{saved.fix}</Box>
                </Box>
              )}
              {!!saved.attachments?.length && (
                <Box>
                  <Box variant="awsui-key-label">
                    Attachments ({saved.attachments.length})
                  </Box>
                  <AttachmentList
                    stage={stage}
                    investigationId={investigation.id}
                    attachments={saved.attachments}
                  />
                </Box>
              )}
            </SpaceBetween>
          </Box>
        ) : (
          <EmptyLine>
            {canEditConclusion || !currentUser
              ? "No conclusion yet."
              : "No conclusion yet. The owner writes it; propose one in a comment."}
          </EmptyLine>
        )}

        <SpaceBetween size="xs">
          <Box variant="awsui-key-label">Evidence ({links.length})</Box>
          <InvestigationLinkList
            entries={links}
            currentUser={currentUser}
            owner={investigation.owner}
            loading={loading}
            emptyState={
              openForm !== "link" && (
                <EmptyLine>No evidence links yet.</EmptyLine>
              )
            }
            onEdit={(entry, { url, label }) =>
              run({
                editLink: {
                  url: entry.url,
                  newUrl: url !== entry.url ? url : undefined,
                  label,
                },
              } as InvestigationUpdateRequest)
            }
            onDelete={(entry) =>
              run({ removeLink: entry.url } as InvestigationUpdateRequest)
            }
          />
          {openForm === "link" && (
            <SpaceBetween size="xs">
              <FormField label="URL" stretch>
                <Input
                  value={linkUrl}
                  onChange={({ detail }) => setLinkUrl(detail.value)}
                  onKeyDown={submitOnModEnter(
                    saveLink,
                    !linkUrl.trim() || loading
                  )}
                  placeholder="https://…"
                  ariaLabel="Link URL"
                  autoFocus
                />
              </FormField>
              <FormField
                label="Label"
                description="Optional. Label defaults to the URL."
                stretch
              >
                <Input
                  value={linkLabel}
                  onChange={({ detail }) => setLinkLabel(detail.value)}
                  onKeyDown={submitOnModEnter(
                    saveLink,
                    !linkUrl.trim() || loading
                  )}
                  placeholder="CR, ticket, log query…"
                  ariaLabel="Link label"
                />
              </FormField>
              <InlineFormActions
                saveLabel="Save link"
                disabled={!linkUrl.trim()}
                loading={loading}
                onCancel={closeForm}
                onSave={saveLink}
              />
            </SpaceBetween>
          )}
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
};

export default InvestigationConclusion;
